import React, { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ConnectionProvider, ConnectionStatus } from "./contexts/ConnectionContext";
import Home from "./pages/Home";
import Profile from "./pages/Profile";
import Leaderboard from "./pages/Leaderboard";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Messages from "./pages/Messages";
import Analytics from "./pages/Analytics";
import Notifications from "./pages/Notifications";
import PostDetail from "./pages/PostDetail";
import Settings from "./pages/Settings";
import AdminTools from "./pages/AdminTools";
import { TooltipProvider } from "@/components/ui/tooltip";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { testConnection } from "@/integrations/supabase/client";
import ErrorBoundary from "./components/ErrorBoundary";
import DirectLogin from "./pages/DirectLogin";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import logger from '@/lib/logger';
import { ensureAllBadgesExist } from "@/lib/supabase";
import { initExperienceSystem, cleanupExperienceSystem } from '@/lib/experienceManager';

// Create a fallback component for connection issues
const ConnectionErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-vibebops-background">
      <div className="w-full max-w-md">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-5 w-5" />
          <AlertTitle>Connection Error</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{error || "Cannot connect to the server. Please try again later."}</p>
            <button 
              onClick={resetErrorBoundary}
              className="px-4 py-2 rounded bg-vibebops-accent text-white hover:bg-vibebops-accent/90"
            >
              Try Again
            </button>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

// Create a component to handle OAuth redirects
const OAuthRedirectHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const processRedirect = async () => {
      try {
        // Log important information for debugging
        logger.info('Processing OAuth redirect', { 
          module: 'OAuthRedirect',
          data: {
            url: window.location.href,
            hash: window.location.hash,
            search: window.location.search,
            origin: window.location.origin,
            isLocalhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          }
        });
        
        // Force refresh the session with the hash fragment
        if (window.location.hash && 
            (window.location.hash.includes('access_token') || 
             window.location.hash.includes('error='))) {
          logger.info('Found hash with auth data, extracting session', { 
            module: 'OAuthRedirect'
          });
          
          // If there's an error in the URL, extract it
          if (window.location.hash.includes('error=')) {
            const errorMessage = decodeURIComponent(
              window.location.hash.split('error=')[1].split('&')[0]
            );
            throw new Error(`OAuth error: ${errorMessage}`);
          }
          
          // Try to extract the session directly from the hash
          try {
            // This will parse the hash and set the session
            await supabase.auth.getSession();
          } catch (e) {
            logger.error('Error extracting session from hash', {
              module: 'OAuthRedirect',
              data: e
            });
          }
        }
        
        // Try multiple times to get the session
        let sessionData = null;
        let sessionError = null;
        
        for (let i = 0; i < 3; i++) {
          try {
            logger.info(`Attempt ${i+1} to get session`, { module: 'OAuthRedirect' });
            const result = await supabase.auth.getSession();
            
            if (result.error) {
              sessionError = result.error;
              logger.warn(`Session error on attempt ${i+1}:`, { 
                module: 'OAuthRedirect', 
                data: result.error
              });
            } else if (result.data?.session) {
              sessionData = result.data;
              break;
            } else {
              logger.warn(`No session found on attempt ${i+1}`, { module: 'OAuthRedirect' });
            }
            
            // Wait a moment before trying again
            await new Promise(resolve => setTimeout(resolve, 1000));
          } catch (err) {
            logger.error(`Exception on session attempt ${i+1}:`, {
              module: 'OAuthRedirect',
              data: err
            });
          }
        }
        
        // Special handling for localhost development environment
        const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        if (sessionData?.session) {
          logger.info('Found valid session after OAuth redirect', { 
            module: 'OAuthRedirect',
            data: { userId: sessionData.session.user.id, isLocalDevelopment }
          });
          
          // Manually update the URL first to clean it up
          window.history.replaceState({}, document.title, '/home');
          
          // Then navigate to home with replace
          navigate('/home', { replace: true });
        } else {
          // For local development, if hash exists but no session was found, try one last approach
          if (isLocalDevelopment && window.location.hash && window.location.hash.includes('access_token')) {
            try {
              logger.info('Local development: trying manual hash extraction', { module: 'OAuthRedirect' });
              
              // Extract tokens manually from hash
              const hashParams = new URLSearchParams(window.location.hash.substring(1));
              const accessToken = hashParams.get('access_token');
              const refreshToken = hashParams.get('refresh_token');
              
              if (accessToken) {
                // Set the session manually with extracted tokens
                await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken || '',
                });
                
                // Check if this worked
                const { data } = await supabase.auth.getSession();
                if (data.session) {
                  // Success! Navigate to home
                  window.history.replaceState({}, document.title, '/home');
                  navigate('/home', { replace: true });
                  return;
                }
              }
            } catch (e) {
              logger.error('Error in local development fallback', { module: 'OAuthRedirect', data: e });
            }
          }
          
          if (sessionError) {
            throw sessionError;
          } else {
            logger.error('No session found after multiple attempts', { module: 'OAuthRedirect' });
            setError('No session was created after authentication. Try again or use email login.');
            // Wait a moment before redirecting to login
            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 3000);
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error('Error in OAuth redirect handler', { 
          module: 'OAuthRedirect',
          data: error
        });
        setError(errorMessage);
        // Wait a moment before redirecting to login
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } finally {
        setIsProcessing(false);
      }
    };
    
    processRedirect();
  }, [navigate]);
  
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-vibebops-background p-4">
        <div className="max-w-md w-full bg-vibebops-card border border-red-500 rounded-lg p-4">
          <h2 className="text-lg font-bold text-red-500 mb-2">Authentication Error</h2>
          <p className="text-vibebops-text mb-4">{error}</p>
          <p className="text-vibebops-textMuted text-sm">Redirecting to login page...</p>
        </div>
      </div>
    );
  }
  
  return <LoadingSpinner fullScreen text="Completing login..." />;
};

const App = () => {
  // Move QueryClient inside the component to fix the hooks error
  const [queryClient] = React.useState(() => new QueryClient());
  const [connectionError, setConnectionError] = useState(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  // Initialize and cleanup experience system
  useEffect(() => {
    // Initialize the experience system
    console.log('Initializing experience system...');
    initExperienceSystem();
    
    // Clean up on unmount
    return () => {
      console.log('Cleaning up experience system...');
      cleanupExperienceSystem();
    };
  }, []);

  // Simple connection check that completes quickly
  useEffect(() => {
    let isMounted = true;
    
    // Enforce a maximum check time - increase to 5 seconds for production
    setTimeout(() => {
      if (isMounted) {
        setIsCheckingConnection(false);
      }
    }, 5000);

    const checkConnection = async () => {
      try {
        // First try to get session to see if auth is working
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        // Then check actual data connection with retries
        let connectionAttempts = 0;
        let result: { connected: boolean, error?: string, data?: any, authIssue?: boolean } = { 
          connected: false, 
          error: 'No connection attempt made yet' 
        };
        
        // Try up to 3 times to connect to Supabase
        while (connectionAttempts < 3 && !result.connected) {
          connectionAttempts++;
          console.log(`Connection attempt ${connectionAttempts}...`);
          
          try {
            result = await testConnection();
            
            if (result.connected) {
              console.log('Connection test successful on attempt', connectionAttempts);
              break;
            } else {
              console.warn(`Connection test failed on attempt ${connectionAttempts}:`, result.error);
              // Wait before next retry
              if (connectionAttempts < 3) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          } catch (err) {
            console.error(`Error during connection attempt ${connectionAttempts}:`, err);
          }
        }
        
        if (!isMounted) return;

        if (!result.connected) {
          console.error('All connection attempts failed:', result.error);
          // Only show error if both session and connection checks fail
          if (!sessionData?.session) {
            setConnectionError(result.error);
          } else {
            // If we have a session but test failed, let's continue anyway
            setConnectionError(null);
          }
        } else {
          setConnectionError(null);
          
          // If connected, ensure all badges exist
          try {
            await ensureAllBadgesExist();
          } catch (err) {
            console.error('Error ensuring badges exist:', err);
            // Continue with app initialization even if badge creation fails
          }
        }
      } catch (err) {
        console.error('Connection test error:', err);
        if (isMounted) {
          setConnectionError(err instanceof Error ? err.message : 'Connection error');
        }
      } finally {
        if (isMounted) {
          setIsCheckingConnection(false);
        }
      }
    };

    checkConnection();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Show a simple loading spinner
  if (isCheckingConnection) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-vibebops-background">
        <div className="animate-spin h-6 w-6 border-2 border-vibebops-accent rounded-full border-t-transparent"></div>
        <div className="mt-2 text-xs text-gray-500">Loading vibebops...</div>
      </div>
    );
  }

  // If connection error, show the fallback
  if (connectionError) {
    return (
      <ConnectionErrorFallback 
        error={connectionError} 
        resetErrorBoundary={() => window.location.reload()}
      />
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ConnectionProvider>
            <AuthProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<OAuthRedirectHandler />} />
                <Route path="/direct-login" element={<DirectLogin />} />
                <Route path="/signup" element={<Navigate to="/login?tab=signup" replace />} />
                <Route path="/home" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Home />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Profile />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/profile/:userId" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Profile />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/leaderboard" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Leaderboard />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/messages" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Messages />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Analytics />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/notifications" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Notifications />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/post/:postId" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <PostDetail />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <Settings />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="/admin-tools" element={
                  <ProtectedRoute>
                    <TooltipProvider>
                      <AdminTools />
                    </TooltipProvider>
                  </ProtectedRoute>
                } />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <ConnectionStatus />
              <Toaster />
              <Sonner />
            </AuthProvider>
          </ConnectionProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
